'use client';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';
import Logo from './Logo';

const navItems = [
  {
    label: 'Features',
    href: '#features',
  },
  {
    label: 'Industries',
    href: '#industries',
  },
  {
    label: 'How It Works',
    href: '#how-it-works',
  },
  {
    label: 'Resources',
    href: '#resources',
    dropdown: [
      { label: 'Blog', href: '#blog' },
    ],
  },
];

export default function Header() {
  const router = useRouter();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'glass shadow-md py-3'
          : 'bg-transparent py-5'
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => router.push('/')}>
            <Logo size="md" animated={false} />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.dropdown && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <a
                  href={item.href}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-[hsl(var(--slate-dark))] hover:text-[hsl(var(--amber))] transition-colors duration-200 rounded-lg hover:bg-[hsl(var(--amber))]/5"
                >
                  {item.label}
                  {item.dropdown && (
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                  )}
                </a>

                {/* Dropdown */}
                {item.dropdown && (
                  <AnimatePresence>
                    {activeDropdown === item.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-48 py-2 bg-white rounded-xl shadow-xl border border-[hsl(var(--border))]"
                      >
                        {item.dropdown.map((dropItem) => (
                          <a
                            key={dropItem.label}
                            href={dropItem.href}
                            className="block px-4 py-2 text-sm text-[hsl(var(--slate-dark))] hover:bg-[hsl(var(--amber))]/5 hover:text-[hsl(var(--amber))] transition-colors"
                          >
                            {dropItem.label}
                          </a>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => router.push('/login')}
              className="text-[hsl(var(--slate-dark))] hover:text-[hsl(var(--amber))] hover:bg-[hsl(var(--amber))]/5"
            >
              Sign In
            </Button>
            <Button
              onClick={() => router.push('/register')}
              className="bg-[hsl(var(--slate-deep))] hover:bg-[hsl(var(--slate-dark))] text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              Sign Up
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[hsl(var(--slate-deep))]" />
            ) : (
              <Menu className="w-6 h-6 text-[hsl(var(--slate-deep))]" />
            )}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-[hsl(var(--border))] shadow-xl"
          >
            <div className="max-w-7xl mx-auto px-6 py-6">
              <div className="flex flex-col gap-2">
                {navItems.map((item, index) => (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="px-4 py-3 text-base font-medium text-[hsl(var(--slate-dark))] hover:text-[hsl(var(--amber))] hover:bg-[hsl(var(--amber))]/5 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </motion.a>
                ))}
                <div className="h-px bg-[hsl(var(--border))] my-4" />
                <Button
                  variant="ghost"
                  onClick={() => {
                    router.push('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="justify-start text-[hsl(var(--slate-dark))]"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => {
                    router.push('/register');
                    setIsMobileMenuOpen(false);
                  }}
                  className="bg-[hsl(var(--slate-deep))] hover:bg-[hsl(var(--slate-dark))] text-white"
                >
                  Sign Up
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
